import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@mui/material";
import { observer } from "mobx-react-lite";

import Language from "../../../services/translation/language";
import {
  SubscriptionPlan,
  ExtraStoragePlan,
  ExtraVACallsPlan,
} from "../../../services/organization-subscription-plans/SubscriptionPlan";
import "./composition.scss";
import { translationService } from "../../../services/translation/TranslationService";
import useThemeService from "../../../services/theme-service/ThemeService";
import { OrganizationSubscriptionPlansStorage } from "../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import UpdateOrganizationSubscriptionPlanInput from "../../../api-models/request/UpdateOrganizationSubscriptionPlanInput";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { dateService } from "../../../services/date-service/DateService";
import GenericSubscriptionPlanInput from "../../../api-models/request/GenericSubscriptionPlanInput";
import { UpdatePlanContentBuilder } from "./modal-content-strategy-builder/UpdatePlanContentBuilder";
import { ModalContentContext } from "./modal-content-strategy-builder/ModalContentContext";
import { DowngradePlanContentBuilder } from "./modal-content-strategy-builder/DowngradePlanContentBuilder";
import UpdateDowngradeSubscriptionPlanInput from "../../../api-models/request/UpdateDowngradeSubscriptionPlanInput";
import ActionButton from "./components/buttons/ActionButton";
import { ExtraStorage } from "./components/functional-components/ExtraStorage";
import { ExtraVideoAnalysis } from "./components/functional-components/ExtraVideoAnalysis";
import { PackagePricingPerSeats } from "./components/functional-components/PackagePricingPerSeats";
import { SubscriptionBenefits } from "./components/functional-components/SubscriptionBenefits";
import { TotalPrice } from "./components/total-price/TotalPrice";
import { SubscriptionDateBanner } from "./components/date-banner/SubscriptionDateBanner";
import { SubscribeButton } from "./components/buttons/SubscribeButton";
import { UpgradePlanContentBuilder } from "./modal-content-strategy-builder/UpgradePlanContentBuilder";
import { CancelDowngradeButton } from "./components/buttons/CancelDowngradeButton";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import { CardsDirtyState } from "./PlansPage";
import DisplayModalContent from "./components/modal/DisplayModalContent";
import { CancelSubscriptionButton } from "./components/buttons/CancelSubscriptionButton";
import { getLocalPrice } from "../../../helpers/MathHelpers";
import { isDateEqualToToday, isDateLessThanToday } from "../../../helpers/DateHelpers";

interface SubscriptionCardProps {
  subscriptionPlan: SubscriptionPlan;
  organizationSubscriptionPlansStorage: OrganizationSubscriptionPlansStorage;
  setCardsDirtyState: (
    value: CardsDirtyState | ((prevVar: CardsDirtyState) => CardsDirtyState)
  ) => void;
}

export const SubscriptionCard = observer((props: SubscriptionCardProps) => {
  const { subscriptionPlan, organizationSubscriptionPlansStorage, setCardsDirtyState } = props;

  const { themeClass } = useThemeService();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();

  const {
    currentActiveSubscription,
    activeSubscriptionForNextBillingPeriod,
    isActiveSubscriptionPaid,
    scheduledSubscription,
    subscriptionWillBeCanceled,
  } = organizationSubscriptionPlansStorage;

  const isActiveInNextBillingPeriod = useMemo(() => {
    return (
      activeSubscriptionForNextBillingPeriod.id === subscriptionPlan.id &&
      !organizationSubscriptionPlansStorage.subscriptionWillBeCanceled
    );
  }, [
    activeSubscriptionForNextBillingPeriod.id,
    organizationSubscriptionPlansStorage.subscriptionWillBeCanceled,
    subscriptionPlan.id,
  ]);

  const isSubscriptionCurrentlyActive = useMemo(() => {
    return currentActiveSubscription.id === subscriptionPlan.id;
  }, [currentActiveSubscription.id, subscriptionPlan.id]);

  const currentSubscriptionWillBeRenewed = useMemo(
    () => activeSubscriptionForNextBillingPeriod.id === currentActiveSubscription.id,
    [activeSubscriptionForNextBillingPeriod.id, currentActiveSubscription.id]
  );

  const [seatsAmount, setSeatsAmount] = useState(
    isActiveInNextBillingPeriod
      ? activeSubscriptionForNextBillingPeriod.activeSubscriptionPackageAmount!
      : subscriptionPlan.minimumNumberOfSeats
  );

  const [extraStorageAmount, setExtraStorageAmount] = useState(
    isActiveInNextBillingPeriod
      ? activeSubscriptionForNextBillingPeriod.extraStoragePackage?.activeSubscriptionPackageAmount || 0
      : 0
  );

  const [extraVACallsAmount, setExtraVACallsAmount] = useState(
    isActiveInNextBillingPeriod
      ? activeSubscriptionForNextBillingPeriod.extraVACalls?.activeSubscriptionPackageAmount || 0
      : 0
  );

  const [addedVACallsForUpgrade, setAddedVACallsForUpgrade] = useState(0);

  const [initialValues] = useState({
    seatsAmount:
      !isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive
        ? currentActiveSubscription.activeSubscriptionPackageAmount
        : seatsAmount,
    extraStorageAmount:
      isActiveInNextBillingPeriod || currentSubscriptionWillBeRenewed
        ? activeSubscriptionForNextBillingPeriod.extraStoragePackage?.activeSubscriptionPackageAmount || 0
        : currentActiveSubscription.extraStoragePackage?.activeSubscriptionPackageAmount || 0,
    extraVACallsAmount:
      isActiveInNextBillingPeriod || currentSubscriptionWillBeRenewed
        ? activeSubscriptionForNextBillingPeriod.extraVACalls?.activeSubscriptionPackageAmount || 0
        : currentActiveSubscription.extraVACalls?.activeSubscriptionPackageAmount || 0,
  });

  const totalPrice = useMemo(() => {
    return (
      seatsAmount * subscriptionPlan.priceAmount +
      seatsAmount * subscriptionPlan.sewerInspectionModule!.priceAmount +
      extraStorageAmount * subscriptionPlan.extraStoragePackage!.priceAmount +
      extraVACallsAmount * subscriptionPlan.extraVACalls!.priceAmount
    );
  }, [seatsAmount, extraStorageAmount, extraVACallsAmount, subscriptionPlan]);

  const getLocalizedPrice = useCallback((price: number) => getLocalPrice(price), []);

  const couldUpgradeTo = useMemo(() => (activeSubscriptionForNextBillingPeriod.id < subscriptionPlan.id)
    , [activeSubscriptionForNextBillingPeriod.id, subscriptionPlan.id]);

  const couldDowngradeTo = useMemo(() => (activeSubscriptionForNextBillingPeriod.id > subscriptionPlan.id)
    , [activeSubscriptionForNextBillingPeriod.id, subscriptionPlan.id]);

  const isActionButtonDisabled = useMemo(() => {
    if (
      organizationSubscriptionPlansStorage.hasScheduledPlanDowngrade() &&
      organizationSubscriptionPlansStorage.isNotCurrentAndScheduledPlanId(subscriptionPlan.id)
    )
      return true;

    const vaCallsAmount =
      activeSubscriptionForNextBillingPeriod.extraVACalls?.activeSubscriptionPackageAmount || 0;
    const activeExtraStorageAmount =
      activeSubscriptionForNextBillingPeriod.extraStoragePackage?.activeSubscriptionPackageAmount ||
      0;

    if (
      isActiveSubscriptionPaid &&
      isActiveInNextBillingPeriod &&
      (seatsAmount !== activeSubscriptionForNextBillingPeriod.activeSubscriptionPackageAmount ||
        extraVACallsAmount !== vaCallsAmount ||
        extraStorageAmount !== activeExtraStorageAmount)
    )
      return false;

    if (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod) {
      const vaCallsAmountEndingSubscription =
        currentActiveSubscription.extraVACalls?.activeSubscriptionPackageAmount || 0;
      const activeExtraStorageAmountEndingSubscription =
        currentActiveSubscription.extraStoragePackage?.activeSubscriptionPackageAmount || 0;

      if (
        seatsAmount !== currentActiveSubscription.activeSubscriptionPackageAmount ||
        extraVACallsAmount !== vaCallsAmountEndingSubscription ||
        extraStorageAmount !== activeExtraStorageAmountEndingSubscription
      )
        return false;

      return true;
    }

    return isActiveInNextBillingPeriod;
  }, [
    organizationSubscriptionPlansStorage,
    subscriptionPlan.id,
    activeSubscriptionForNextBillingPeriod.extraVACalls?.activeSubscriptionPackageAmount,
    activeSubscriptionForNextBillingPeriod.extraStoragePackage?.activeSubscriptionPackageAmount,
    activeSubscriptionForNextBillingPeriod.activeSubscriptionPackageAmount,
    isActiveSubscriptionPaid,
    isActiveInNextBillingPeriod,
    seatsAmount,
    extraVACallsAmount,
    extraStorageAmount,
    isSubscriptionCurrentlyActive,
    currentActiveSubscription.extraVACalls?.activeSubscriptionPackageAmount,
    currentActiveSubscription.extraStoragePackage?.activeSubscriptionPackageAmount,
    currentActiveSubscription.activeSubscriptionPackageAmount,
  ]);

  const updateExistingDowngrade = useMemo(() => {
    return (
      isActiveSubscriptionPaid &&
      isActiveInNextBillingPeriod &&
      scheduledSubscription &&
      scheduledSubscription?.id! < currentActiveSubscription.id
    );
  }, [
    scheduledSubscription,
    currentActiveSubscription.id,
    isActiveSubscriptionPaid,
    isActiveInNextBillingPeriod
  ]);

  const requestDowngrade = useMemo(() => {
    return (
      isActiveSubscriptionPaid &&
      !isActiveInNextBillingPeriod &&
      !isSubscriptionCurrentlyActive &&
      couldDowngradeTo
    );
  }, [
    isActiveSubscriptionPaid,
    couldDowngradeTo,
    isActiveInNextBillingPeriod,
    isSubscriptionCurrentlyActive,
  ]);

  const getActionButtonTextKey = useCallback(() => {
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    )
      return "SubscriptionPlans.card.update-plan";
    if (isActiveSubscriptionPaid && couldUpgradeTo) return "SubscriptionPlans.card.upgrade";
    if (isActiveSubscriptionPaid && couldDowngradeTo) return "SubscriptionPlans.card.downgrade";
  }, [
    isActiveSubscriptionPaid,
    isActiveInNextBillingPeriod,
    isSubscriptionCurrentlyActive,
    couldUpgradeTo,
    couldDowngradeTo,
  ]);

  const getConfirmationButtonTextKey = useCallback(() => {
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    )
      return "SubscriptionPlans.card.update-plan";
    if (isActiveSubscriptionPaid && couldUpgradeTo) return "SubscriptionPlans.card.upgrade";
    if (requestDowngrade || updateExistingDowngrade)
      return "SubscriptionPlans.card.request-downgrade";
  }, [
    isActiveSubscriptionPaid,
    couldUpgradeTo,
    isActiveInNextBillingPeriod,
    requestDowngrade,
    isSubscriptionCurrentlyActive,
    updateExistingDowngrade,
  ]);

  const getConfirmationModalTitleKey = useCallback(() => {
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    )
      return "SubscriptionPlans.card.update-plan";
    if (isActiveSubscriptionPaid && couldUpgradeTo) return "SubscriptionPlans.card.upgrade";
    if (requestDowngrade || updateExistingDowngrade)
      return "SubscriptionPlans.card.request-downgrade";
  }, [
    isActiveSubscriptionPaid,
    couldUpgradeTo,
    isActiveInNextBillingPeriod,
    requestDowngrade,
    isSubscriptionCurrentlyActive,
    updateExistingDowngrade,
  ]);

  const getConfirmationModalContent = useCallback(() => {
    let modalContentStrategy = undefined;
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    ) {
      modalContentStrategy = new UpdatePlanContentBuilder(
        seatsAmount,
        extraStorageAmount,
        extraVACallsAmount,
        currentActiveSubscription,
        scheduledSubscription,
        totalPrice,
        dateService.formatDate(currentActiveSubscription.nextBillingDate!)
      );
    } else if (isActiveSubscriptionPaid && couldUpgradeTo)
      modalContentStrategy = new UpgradePlanContentBuilder(
        seatsAmount,
        extraStorageAmount,
        extraVACallsAmount,
        currentActiveSubscription,
        scheduledSubscription,
        totalPrice,
        dateService.formatDate(currentActiveSubscription.nextBillingDate!),
        addedVACallsForUpgrade,
        subscriptionPlan.name
      );
    else if (requestDowngrade || updateExistingDowngrade)
      modalContentStrategy = new DowngradePlanContentBuilder(
        seatsAmount,
        extraStorageAmount,
        extraVACallsAmount,
        totalPrice,
        dateService.formatDate(currentActiveSubscription.nextBillingDate!),
        subscriptionPlan.name,
        currentActiveSubscription
      );

    if (!modalContentStrategy) return <></>;

    const modalContentContext = new ModalContentContext(modalContentStrategy);
    const content = modalContentContext.getModalContent();

    return <DisplayModalContent content={content} />;
  }, [
    addedVACallsForUpgrade,
    couldUpgradeTo,
    currentActiveSubscription,
    extraStorageAmount,
    extraVACallsAmount,
    isActiveInNextBillingPeriod,
    isActiveSubscriptionPaid,
    isSubscriptionCurrentlyActive,
    requestDowngrade,
    scheduledSubscription,
    seatsAmount,
    subscriptionPlan.name,
    totalPrice,
    updateExistingDowngrade,
  ]);

  const getActionButtonClassName = useCallback(() => {
    if (isActiveSubscriptionPaid && isActiveInNextBillingPeriod) return themeClass(["btn-big"]);
    if (isActiveSubscriptionPaid && couldUpgradeTo) return themeClass(["btn-big"]);
    if (isActiveSubscriptionPaid && couldDowngradeTo)
      return themeClass(["btn-big", "fill-color-ghost"]);

    return themeClass(["btn-big"]);
  }, [
    isActiveSubscriptionPaid,
    couldDowngradeTo,
    couldUpgradeTo,
    isActiveInNextBillingPeriod,
    themeClass,
  ]);

  const handleUpdatePlanConfirmationClick = useCallback(async () => {
    try {
      const input: UpdateOrganizationSubscriptionPlanInput = {
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.updateSubscriptionPlan(input);
      snackbarNotificationsService.showSuccess("SubscriptionPlans.card.update-plan.seats-success");
    } catch (error) {
      const errorContext = { seatsCount: seatsAmount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.subscription-plan.update-seats");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    seatsAmount,
    extraVACallsAmount,
    extraStorageAmount,
    organizationSubscriptionPlansStorage,
    handleErrorInSnackbar,
  ]);

  const handleDowngradePlanConfirmationClick = useCallback(async () => {
    try {
      const input: GenericSubscriptionPlanInput = {
        packageId: subscriptionPlan.id,
        sewerInspectionModuleId: subscriptionPlan.sewerInspectionModule!.id,
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.downgradeSubscriptionPlan(
        input
      );
      snackbarNotificationsService.showSuccess("SubscriptionPlans.card.downgrade-plan.success");
    } catch (error) {
      const errorContext = { seatsCount: seatsAmount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.subscription-plan.downgrade");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    subscriptionPlan.id,
    subscriptionPlan.sewerInspectionModule,
    seatsAmount,
    extraVACallsAmount,
    extraStorageAmount,
    organizationSubscriptionPlansStorage,
    handleErrorInSnackbar,
  ]);

  const handleUpdateDowngradePlanConfirmationClick = useCallback(async () => {
    try {
      const input: UpdateDowngradeSubscriptionPlanInput = {
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.updateDowngradeSubscriptionPlan(
        input
      );
      snackbarNotificationsService.showSuccess(
        "SubscriptionPlans.card.update-downgrade-plan.success"
      );
    } catch (error) {
      const errorContext = { seatsCount: seatsAmount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.subscription-plan.update-downgrade");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    seatsAmount,
    extraVACallsAmount,
    extraStorageAmount,
    organizationSubscriptionPlansStorage,
    handleErrorInSnackbar,
  ]);

  const handleUpgradePlanConfirmationClick = useCallback(async () => {
    try {
      const input: GenericSubscriptionPlanInput = {
        packageId: subscriptionPlan.id,
        sewerInspectionModuleId: subscriptionPlan.sewerInspectionModule!.id,
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.upgradeSubscriptionPlan(input);
      snackbarNotificationsService.showSuccess("SubscriptionPlans.card.upgrade-plan.success");
    } catch (error) {
      const errorContext = { seatsCount: seatsAmount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.subscription-plan.upgrade");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    extraStorageAmount,
    extraVACallsAmount,
    handleErrorInSnackbar,
    organizationSubscriptionPlansStorage,
    seatsAmount,
    subscriptionPlan.id,
    subscriptionPlan.sewerInspectionModule,
  ]);

  const handleSubscribeClickValidation = useCallback(async () => {
    try {
      const input: GenericSubscriptionPlanInput = {
        packageId: subscriptionPlan.id,
        sewerInspectionModuleId: subscriptionPlan.sewerInspectionModule!.id,
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.checkSubscriptionPlanIsValid(input);
      return true;
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error");
      applicationError.withModalTitleKey("SubscriptionPlans.subscribe-failed");
      handleErrorInModal(applicationError);
      return false;
    }
  }, [
    extraStorageAmount,
    extraVACallsAmount,
    handleErrorInModal,
    organizationSubscriptionPlansStorage,
    seatsAmount,
    subscriptionPlan.id,
    subscriptionPlan.sewerInspectionModule,
  ]);

  const handleUpdatePlanClickValidation = useCallback(async () => {
    try {
      const input: UpdateOrganizationSubscriptionPlanInput = {
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
      };
      await organizationSubscriptionPlansStorage.checkSubscriptionPlanUpdateIsValid(
        input
      );
      return true;
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error");
      applicationError.withModalTitleKey("SubscriptionPlans.update-failed");
      handleErrorInModal(applicationError);
      return false;
    }
  }, [
    seatsAmount,
    extraVACallsAmount,
    extraStorageAmount,
    organizationSubscriptionPlansStorage,
    handleErrorInModal,
  ]);

  const handleUpgradeToPlanClickValidation = useCallback(async () => {
    try {
      const input: UpdateOrganizationSubscriptionPlanInput = {
        fullRightSeats: seatsAmount,
        extraVACalls: extraVACallsAmount,
        extraStorage: extraStorageAmount,
        packageId: subscriptionPlan.id,
      };
      const addedVACalls =
        await organizationSubscriptionPlansStorage.checkSubscriptionPlanUpdateIsValid(
          input
        );
      setAddedVACallsForUpgrade(addedVACalls);
      return true;
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error");
      applicationError.withModalTitleKey("SubscriptionPlans.upgrade-failed");
      handleErrorInModal(applicationError);
      return false;
    }
  }, [
    extraStorageAmount,
    extraVACallsAmount,
    handleErrorInModal,
    organizationSubscriptionPlansStorage,
    seatsAmount,
    subscriptionPlan.id,
  ]);

  const getValidationActionClick = useCallback(async () => {
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    )
      return await handleUpdatePlanClickValidation();
    if (isActiveSubscriptionPaid && couldUpgradeTo) return handleUpgradeToPlanClickValidation();

    if (!isActiveSubscriptionPaid) return handleSubscribeClickValidation();

    return true;
  }, [
    couldUpgradeTo,
    handleSubscribeClickValidation,
    handleUpdatePlanClickValidation,
    handleUpgradeToPlanClickValidation,
    isActiveInNextBillingPeriod,
    isActiveSubscriptionPaid,
    isSubscriptionCurrentlyActive,
  ]);

  const getConfirmationActionClick = useCallback(async () => {
    if (
      (isActiveSubscriptionPaid && isActiveInNextBillingPeriod && isSubscriptionCurrentlyActive) ||
      (isSubscriptionCurrentlyActive && !isActiveInNextBillingPeriod)
    )
      await handleUpdatePlanConfirmationClick();
    else if (isActiveSubscriptionPaid && couldUpgradeTo) await handleUpgradePlanConfirmationClick();
    else if (requestDowngrade) await handleDowngradePlanConfirmationClick();
    else if (updateExistingDowngrade) await handleUpdateDowngradePlanConfirmationClick();
  }, [
    couldUpgradeTo,
    handleDowngradePlanConfirmationClick,
    handleUpdateDowngradePlanConfirmationClick,
    handleUpdatePlanConfirmationClick,
    handleUpgradePlanConfirmationClick,
    isActiveInNextBillingPeriod,
    isActiveSubscriptionPaid,
    isSubscriptionCurrentlyActive,
    requestDowngrade,
    updateExistingDowngrade,
  ]);

  const getTooltipForActionButton = useCallback(() => {
    if (!organizationSubscriptionPlansStorage.hasScheduledPlanDowngrade())
      return;
    if (organizationSubscriptionPlansStorage.currentActiveSubscription.id === subscriptionPlan.id)
      return;
    if (organizationSubscriptionPlansStorage.activeSubscriptionForNextBillingPeriod.id < subscriptionPlan.id)
      return "SubscriptionPlans.card.cancel-downgrade-tooltip.for-upgrade";
    if (organizationSubscriptionPlansStorage.activeSubscriptionForNextBillingPeriod.id > subscriptionPlan.id)
      return "SubscriptionPlans.card.cancel-downgrade-tooltip.for-downgrade";
  }, [
    organizationSubscriptionPlansStorage,
    subscriptionPlan.id
  ]);

  useEffect(() => {
    setCardsDirtyState((prev) => {
      return {
        ...prev,
        [subscriptionPlan.id]:
          seatsAmount !== initialValues.seatsAmount ||
          extraStorageAmount !== initialValues.extraStorageAmount ||
          extraVACallsAmount !== initialValues.extraVACallsAmount,
      };
    });
  }, [
    initialValues,
    seatsAmount,
    extraStorageAmount,
    extraVACallsAmount,
    setCardsDirtyState,
    subscriptionPlan.id,
  ]);

  const isUnsubscribeActivated = useMemo(() => {
    if (!subscriptionPlan.isEnterprise) {
      return true;
    }
    if (!currentActiveSubscription.unsubscribeActivationDate) {
      return false;
    }
    return isDateLessThanToday(currentActiveSubscription.unsubscribeActivationDate!) ||
      isDateEqualToToday(currentActiveSubscription.unsubscribeActivationDate!);
  }, [currentActiveSubscription.unsubscribeActivationDate, subscriptionPlan.isEnterprise]);

  return (
    <Card
      className={
        isActiveInNextBillingPeriod ? "subscription-plan-card active" : "subscription-plan-card"
      }
      variant="outlined"
    >
      <CardHeader
        className="header"
        title={translationService.translate(`SubscriptionPlans.card.${subscriptionPlan.name}`)}
      />
      <SubscriptionDateBanner
        nextBillingDate={currentActiveSubscription.nextBillingDate}
        isActiveInNextBillingPeriod={isActiveInNextBillingPeriod}
        isSubscriptionCurrentlyActive={isSubscriptionCurrentlyActive}
        subscriptionWillBeCanceled={subscriptionWillBeCanceled}
      />
      <CardContent className="content">
        <div className="plan-description">
          {translationService.language === Language.english
            ? subscriptionPlan.englishDescription
            : subscriptionPlan.germanDescription}
        </div>
        <div className="included-benefits-container">
          <div className="medium-label">
            {translationService.translate("SubscriptionPlans.card.package-include")}
          </div>
          <SubscriptionBenefits
            subscriptionPlanDetails={subscriptionPlan}
          />
          <PackagePricingPerSeats
            isActiveInNextBillingPeriod={isActiveInNextBillingPeriod}
            isCurrentlyActive={isSubscriptionCurrentlyActive}
            subscriptionPlan={
              isActiveInNextBillingPeriod
                ? activeSubscriptionForNextBillingPeriod
                : isSubscriptionCurrentlyActive
                  ? currentActiveSubscription
                  : subscriptionPlan
            }
            currentSubscriptionWillBeRenewed={currentSubscriptionWillBeRenewed}
            baseSubscriptionPlan={subscriptionPlan}
            totalSeatsAmountChanged={setSeatsAmount}
            nextBillingDate={currentActiveSubscription.nextBillingDate}
            getLocalizedPrice={getLocalizedPrice}
          />
        </div>
        <ExtraStorage
          isActiveInNextBillingPeriod={isActiveInNextBillingPeriod}
          extraStorage={
            isActiveInNextBillingPeriod || currentSubscriptionWillBeRenewed
              ? (activeSubscriptionForNextBillingPeriod.extraStoragePackage as ExtraStoragePlan)
              : (currentActiveSubscription.extraStoragePackage as ExtraStoragePlan)
          }
          currentSubscriptionWillBeRenewed={currentSubscriptionWillBeRenewed}
          baseExtraStoragePlan={subscriptionPlan.extraStoragePackage as ExtraStoragePlan}
          totalExtraStorageAmountChanged={setExtraStorageAmount}
          nextBillingDate={currentActiveSubscription.nextBillingDate}
          currentPlanWillEndAndIsNotCanceled={
            isSubscriptionCurrentlyActive &&
            !isActiveInNextBillingPeriod &&
            !subscriptionWillBeCanceled
          }
          getLocalizedPrice={getLocalizedPrice}
        />
        <ExtraVideoAnalysis
          isActiveInNextBillingPeriod={isActiveInNextBillingPeriod}
          extraVACalls={
            isActiveInNextBillingPeriod || currentSubscriptionWillBeRenewed
              ? (activeSubscriptionForNextBillingPeriod.extraVACalls as ExtraVACallsPlan)
              : (currentActiveSubscription.extraVACalls as ExtraVACallsPlan)
          }
          currentSubscriptionWillBeRenewed={currentSubscriptionWillBeRenewed}
          baseExtraVACallsPlan={subscriptionPlan.extraVACalls as ExtraVACallsPlan}
          totalExtraVACallsAmountChanged={setExtraVACallsAmount}
          nextBillingDate={currentActiveSubscription.nextBillingDate}
          currentPlanWillEndAndIsNotCanceled={
            isSubscriptionCurrentlyActive &&
            !isActiveInNextBillingPeriod &&
            !subscriptionWillBeCanceled
          }
          getLocalizedPrice={getLocalizedPrice}
        />
        <TotalPrice totalPrice={getLocalizedPrice(totalPrice)} />
        <div className="action-button-container">
          {(!isActiveSubscriptionPaid || subscriptionWillBeCanceled) && (
            <SubscribeButton
              seatsAmount={seatsAmount}
              extraStorageAmount={extraStorageAmount}
              extraVACallsAmount={extraVACallsAmount}
              subscriptionPlan={subscriptionPlan}
              organizationSubscriptionPlansStorage={organizationSubscriptionPlansStorage}
              confirmationButtonTextKey={getConfirmationButtonTextKey()!}
              confirmationModalTitleKey={getConfirmationModalTitleKey()!}
              onValidationActionClick={getValidationActionClick}
            >
              {getConfirmationModalContent()}
            </SubscribeButton>
          )}
          {isActiveSubscriptionPaid && !subscriptionWillBeCanceled && (
            <ActionButton
              disabled={isActionButtonDisabled}
              confirmationAction={getConfirmationActionClick}
              className={getActionButtonClassName()}
              textKey={getActionButtonTextKey()!}
              confirmationButtonTextKey={getConfirmationButtonTextKey()!}
              confirmationModalTitleKey={getConfirmationModalTitleKey()!}
              onValidationActionClick={getValidationActionClick}
              tooltipKey={getTooltipForActionButton()}
            >
              {getConfirmationModalContent()}
            </ActionButton>
          )}
          {isActiveInNextBillingPeriod && !isSubscriptionCurrentlyActive && (
            <CancelDowngradeButton
              organizationSubscriptionPlansStorage={organizationSubscriptionPlansStorage}
            />
          )}
          {isActiveInNextBillingPeriod &&
            isSubscriptionCurrentlyActive &&
            !subscriptionWillBeCanceled &&
            isUnsubscribeActivated &&(
              <CancelSubscriptionButton
                organizationSubscriptionPlansStorage={organizationSubscriptionPlansStorage}
              />
            )}
        </div>
      </CardContent>
    </Card>
  );
});
